import "./bug-inspect-diff.css"

import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import {
	IconCheckOutline18 as CheckIcon,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronRightOutline18 as ChevronRight,
	IconCodePullRequestOutline18 as PullRequestIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Badge, Button } from "@productlane/ui"

import type { PatchLineKind } from "./diff-highlight"
import type { RouterOutputs } from "@/lib/api"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SETTINGS_CARD_NOTICE_CLASS } from "../../../../../settings/settings-switches/settings-container"
import { parsePatch } from "./diff-highlight"

export type RunCard = NonNullable<RouterOutputs["bugInspect"]["run"]>
type DiffFile = RouterOutputs["bugInspect"]["diff"]["files"][number]

const CARD_CLASS =
	"ml-[42px] mt-1 grid gap-3 rounded-[10px] bg-white p-4 text-sm shadow-card dark:bg-gray-850 dark:shadow-card-dark"

export function testsAddedLabel(count: number): string {
	return count === 1 ? "1 test added" : `${count} tests added`
}

export function approvedLine(
	card: Pick<RunCard, "reviewerName" | "customerNotified">,
) {
	const name = card.reviewerName ?? "A teammate"
	return card.customerNotified
		? `${name} approved the fix. Bug Inspect messaged the customer.`
		: `${name} approved the fix. This report has no chat, so no customer message went out.`
}

export function rejectedLine(card: Pick<RunCard, "reviewerName">) {
	return `${card.reviewerName ?? "A teammate"} rejected the fix and the branch was deleted.`
}

function diffTotals(files: readonly DiffFile[]) {
	return {
		additions: files.reduce((sum, file) => sum + file.additions, 0),
		deletions: files.reduce((sum, file) => sum + file.deletions, 0),
		filesLabel: files.length === 1 ? "1 file" : `${files.length} files`,
	}
}

export function diffSummary(files: readonly DiffFile[]) {
	const { additions, deletions, filesLabel } = diffTotals(files)
	return `+${additions} −${deletions} · ${filesLabel}`
}

export function DiffStats({ files }: { files: readonly DiffFile[] }) {
	const { additions, deletions, filesLabel } = diffTotals(files)
	return (
		<span className="whitespace-nowrap tabular-nums">
			<span className="text-green-700 dark:text-green-400">+{additions}</span>{" "}
			<span className="text-red-700 dark:text-red-400">−{deletions}</span>
			<span className="text-tertiary"> · {filesLabel}</span>
		</span>
	)
}

const LINE_CLASS: Record<PatchLineKind, string> = {
	added: "bg-green-50 dark:bg-green-500/10",
	removed: "bg-red-50 dark:bg-red-500/10",
	context: "",
	hunk: "bg-gray-50 text-tertiary dark:bg-gray-800/60",
	note: "text-tertiary",
}

const MARKER_CLASS: Partial<Record<PatchLineKind, string>> = {
	added: "text-green-700 dark:text-green-400",
	removed: "text-red-700 dark:text-red-400",
}

function PatchLines({ patch, filename }: { patch: string; filename: string }) {
	const lines = useMemo(() => parsePatch(patch, filename), [patch, filename])
	return (
		<div className="diff-code overflow-x-auto border-t border-gray-200 font-mono text-xs leading-5 dark:border-gray-700/80">
			<div className="w-max min-w-full py-1">
				{lines.map((line, index) => (
					<div
						key={index}
						className={cn("flex whitespace-pre pr-4", LINE_CLASS[line.kind])}
					>
						<span
							className={cn(
								"w-7 flex-none select-none text-center text-tertiary",
								MARKER_CLASS[line.kind],
							)}
						>
							{line.marker}
						</span>
						{/* hljs escapes the code, and parsePatch escapes lines it skips. */}
						<span dangerouslySetInnerHTML={{ __html: line.html || " " }} />
					</div>
				))}
			</div>
		</div>
	)
}

export function DiffFiles({
	runId,
	defaultExpanded = false,
}: {
	runId: string
	defaultExpanded?: boolean
}) {
	const diffQuery = api.bugInspect.diff.useQuery(
		{ runId },
		{ staleTime: 60_000 },
	)
	// Files the reviewer flipped away from the default, so every file can open.
	const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set())
	const toggle = (filename: string) =>
		setToggled((current) => {
			const next = new Set(current)
			if (!next.delete(filename)) next.add(filename)
			return next
		})

	if (diffQuery.isLoading) {
		return (
			<div className="text-xs text-tertiary">
				Loading the diff from GitHub...
			</div>
		)
	}
	if (diffQuery.isError || !diffQuery.data) {
		return (
			<Button
				variant="ghost"
				className="h-auto min-h-0 justify-start px-0 text-xs text-tertiary"
				onClick={() => void diffQuery.refetch()}
			>
				GitHub couldn&apos;t return the diff. Try again.
			</Button>
		)
	}

	return (
		<div className="overflow-hidden rounded-md ring-1 ring-inset ring-gray-200 dark:ring-gray-700/80">
			{diffQuery.data.files.map((file) => {
				const isOpen = defaultExpanded !== toggled.has(file.filename)
				return (
					<div
						key={file.filename}
						className="border-t border-gray-200 first:border-t-0 dark:border-gray-700/80"
					>
						<Button
							variant="ghost"
							iconRight={isOpen ? ChevronDown : ChevronRight}
							className="h-8 w-full max-w-none justify-between rounded-none bg-gray-50 px-2.5 font-mono text-xs font-normal dark:bg-gray-800 dark:hover:bg-gray-750"
							wrapperClassName="w-full"
							onClick={() => toggle(file.filename)}
						>
							<span className="min-w-0 truncate text-left">
								{file.filename}
							</span>
							<span className="ml-auto flex-none pl-3">
								<span className="text-green-700 dark:text-green-400">
									+{file.additions}
								</span>{" "}
								<span className="text-red-700 dark:text-red-400">
									−{file.deletions}
								</span>
							</span>
						</Button>
						{isOpen && file.patch ? (
							<PatchLines patch={file.patch} filename={file.filename} />
						) : null}
					</div>
				)
			})}
		</div>
	)
}

function PullRequestTitle({
	card,
	struck = false,
}: {
	card: RunCard
	struck?: boolean
}) {
	const title = card.prTitle ?? `Pull request #${card.prNumber}`
	const label = card.prTitle ? `#${card.prNumber} ${title}` : title
	return card.prUrl ? (
		<a
			href={card.prUrl}
			target="_blank"
			rel="noopener noreferrer"
			className={cn(
				"truncate font-medium hover:underline",
				struck && "text-tertiary line-through",
			)}
		>
			{label}
		</a>
	) : (
		<span
			className={cn(
				"truncate font-medium",
				struck && "text-tertiary line-through",
			)}
		>
			{label}
		</span>
	)
}

export function HarnessBadges({
	card,
	size = "sm",
	children,
}: {
	card: RunCard | null | undefined
	size?: "sm" | "default"
	// Badges that lead the row, such as the card type on the Agent page.
	children?: ReactNode
}) {
	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{children}
			{card ? (
				<>
					<Badge variant="green" size={size}>
						<CheckIcon className="h-3 w-3 flex-none" />
						Reproduced on {card.defaultBranch ?? "the default branch"}
					</Badge>
					<Badge variant="green" size={size}>
						<CheckIcon className="h-3 w-3 flex-none" />
						Passes on this branch
					</Badge>
					{card.testsAdded !== null ? (
						<Badge variant="light-gray" size={size}>
							{testsAddedLabel(card.testsAdded)}
						</Badge>
					) : null}
				</>
			) : null}
		</div>
	)
}

export function useBugInspectReview(
	runId: string,
	{ onReviewed }: { onReviewed?: () => void } = {},
) {
	const utils = api.useUtils()
	const onSettled = () => void utils.bugInspect.run.invalidate({ runId })
	const onError = (error: { message: string }) =>
		toast.info({ title: error.message, icon: PullRequestIcon })
	const approve = api.bugInspect.approve.useMutation({
		onSuccess: () => {
			toast.success({ title: "Fix approved", icon: PullRequestIcon })
			onReviewed?.()
		},
		onError,
		onSettled,
	})
	const reject = api.bugInspect.reject.useMutation({
		onSuccess: () => {
			toast.success({ title: "Fix rejected", icon: PullRequestIcon })
			onReviewed?.()
		},
		onError,
		onSettled,
	})
	return { approve, reject, busy: approve.isLoading || reject.isLoading }
}

// The teammate's review of a draft fix. Only the reviewing teammates see it;
// the customer hears from it once, after an approval, through the follow-up.
export function BugInspectReviewCard({ runId }: { runId: string }) {
	const runQuery = api.bugInspect.run.useQuery({ runId })
	const diffQuery = api.bugInspect.diff.useQuery(
		{ runId },
		{ staleTime: 60_000 },
	)
	const { approve, reject, busy } = useBugInspectReview(runId)

	const card = runQuery.data
	if (!card) return null

	if (card.reviewState === "APPROVED") {
		return (
			<div className={CARD_CLASS}>
				<div
					className={cn(
						SETTINGS_CARD_NOTICE_CLASS,
						"flex items-center gap-2 bg-gray-50 text-secondary dark:bg-gray-800",
					)}
				>
					<CheckIcon className="h-4 w-4 flex-none text-green-600 dark:text-green-400" />
					<span className="min-w-0">{approvedLine(card)}</span>
				</div>
				<div className="flex min-w-0 items-center gap-2.5">
					<PullRequestIcon className="h-4 w-4 flex-none text-tertiary" />
					<div className="grid min-w-0">
						<PullRequestTitle card={card} />
						<span className="text-xs text-tertiary">
							The pull request is ready for code review in GitHub.
						</span>
					</div>
				</div>
			</div>
		)
	}

	if (card.reviewState === "REJECTED") {
		return (
			<div className={CARD_CLASS}>
				<div className="flex min-w-0 items-center gap-2.5">
					<PullRequestIcon className="h-4 w-4 flex-none text-tertiary" />
					<div className="grid min-w-0 flex-1">
						<PullRequestTitle card={card} struck />
						<span className="text-xs text-tertiary">{rejectedLine(card)}</span>
					</div>
					<Badge variant="light-gray" size="sm">
						Closed
					</Badge>
				</div>
			</div>
		)
	}

	return (
		<div className={CARD_CLASS}>
			<div className="flex min-w-0 items-center gap-2.5">
				<PullRequestIcon className="h-4 w-4 flex-none text-tertiary" />
				<div className="grid min-w-0 flex-1">
					<PullRequestTitle card={card} />
					<span className="min-h-4 font-mono text-xs text-tertiary">
						{diffQuery.data
							? `#${card.prNumber} · ${diffSummary(diffQuery.data.files)}`
							: `#${card.prNumber}`}
					</span>
				</div>
				<Badge variant="amber" size="sm">
					<span className="size-1.5 flex-none rounded-full bg-amber-500" />
					Draft
				</Badge>
			</div>
			<HarnessBadges card={card} />
			<DiffFiles runId={runId} />
			<div className="flex flex-wrap justify-end gap-1.5">
				{card.linearUrl ? (
					<Button
						variant="ghost"
						onClick={() =>
							window.open(card.linearUrl!, "_blank", "noopener,noreferrer")
						}
					>
						Review in Linear
					</Button>
				) : null}
				<Button
					variant="outline"
					isLoading={reject.isLoading}
					disabled={busy}
					onClick={() => reject.mutate({ runId })}
				>
					Reject
				</Button>
				<Button
					variant="default"
					isLoading={approve.isLoading}
					disabled={busy}
					onClick={() => approve.mutate({ runId })}
				>
					Approve
				</Button>
			</div>
		</div>
	)
}
